import React, { useMemo, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Platform,
  useWindowDimensions,
  Linking,
} from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import { WebView } from 'react-native-webview';
import { fetchPageBySlug } from '../../services/cmsService';
import { useTheme } from '../../contexts/ThemeContext';
import LeftSidebar from '../../components/LeftSidebar';
import { fontStyle } from '../../config/fonts';
import { LanguageCode } from '../../types';

const WEB_SIDEBAR_BREAKPOINT = 1024;

export default function CMSPageScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const { colors, isDark } = useTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const showSidebars =
    Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT;

  // Get current language for CMS content
  const currentLanguage = (i18n.language || 'en') as LanguageCode;

  const {
    data: page,
    isLoading,
    isError,
  } = useQuery({
    // Include language in query key to refetch when language changes
    queryKey: ['cms_page', slug, currentLanguage],
    queryFn: () => fetchPageBySlug(slug!, currentLanguage),
    enabled: !!slug,
  });

  // Generate theme-aware CSS
  const themeCSS = useMemo(
    () => `
    * {
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      font-size: 16px;
      line-height: 1.6;
      color: ${colors.text};
      background-color: ${colors.background};
      padding: 16px;
      margin: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    h1, h2, h3, h4, h5, h6 {
      color: ${colors.text};
      margin-top: 24px;
      margin-bottom: 16px;
      font-weight: 600;
      line-height: 1.25;
    }
    h1 { font-size: 28px; }
    h2 { font-size: 24px; }
    h3 { font-size: 20px; }
    h4 { font-size: 18px; }
    p {
      margin-bottom: 16px;
    }
    a {
      color: ${colors.primary};
      text-decoration: underline;
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 12px 0;
    }
    ul, ol {
      padding-left: 24px;
      margin-bottom: 16px;
    }
    /* Fix for Quill 2+ bullet lists - they use <ol data-list="bullet"> */
    ol[data-list="bullet"] {
      list-style-type: disc !important;
    }
    ol[data-list="bullet"] li {
      list-style-type: disc !important;
    }
    /* Ensure ordered lists still show numbers */
    ol[data-list="ordered"] {
      list-style-type: decimal !important;
    }
    ol[data-list="ordered"] li {
      list-style-type: decimal !important;
    }
    li {
      margin-bottom: 8px;
    }
    blockquote {
      border-left: 4px solid ${colors.primary};
      margin: 16px 0;
      padding: 12px 16px;
      background-color: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
      border-radius: 0 8px 8px 0;
    }
    blockquote p {
      margin: 0;
    }
    code {
      background-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'SF Mono', Monaco, 'Courier New', monospace;
      font-size: 14px;
    }
    pre {
      background-color: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 16px 0;
    }
    pre code {
      background: none;
      padding: 0;
    }
    hr {
      border: none;
      border-top: 1px solid ${colors.border};
      margin: 24px 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
    }
    th, td {
      border: 1px solid ${colors.border};
      padding: 12px;
      text-align: left;
    }
    th {
      background-color: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
      font-weight: 600;
    }
    iframe {
      max-width: 100%;
      border-radius: 8px;
      margin: 12px 0;
    }
    /* Task list styles */
    input[type="checkbox"] {
      margin-right: 8px;
    }
  `,
    [colors, isDark]
  );

  // Generate full HTML document
  const generateHtmlDocument = useCallback(
    (content: string) => `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
        <style>${themeCSS}</style>
      </head>
      <body>${content}</body>
    </html>
  `,
    [themeCSS]
  );

  // Handle link clicks in WebView
  const handleWebViewRequest = useCallback(
    (event: { url: string }) => {
      const { url } = event;
      // Allow initial load and same-page navigation
      if (url === 'about:blank' || url.startsWith('data:')) {
        return true;
      }
      // Open external links in browser
      Linking.openURL(url);
      return false;
    },
    []
  );

  // Generate SEO meta tags (safe to compute even if page is undefined)
  const seoTitle = page?.seo?.metaTitle || page?.title || '';
  const seoDescription = page?.seo?.metaDescription || '';
  const seoKeywords = page?.seo?.metaKeywords?.join(', ') || '';
  const ogImage = page?.seo?.ogImage || (page?.seo?.useFeatureImage !== false && page?.featuredImage ? page.featuredImage : undefined);
  const canonicalUrl = page?.seo?.canonicalUrl;
  const noIndex = page?.seo?.noIndex === true;

  // Update document head with SEO meta tags (web only)
  // This hook must be called unconditionally to follow Rules of Hooks
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Handle error state
    if (isError || !page) {
      document.title = t('errors.pageNotFound') || 'Page not found';
      return;
    }

    // Handle success state with SEO

    // Update title
    document.title = seoTitle;

    // Helper function to update or create meta tag
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper function to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Update meta description
    if (seoDescription) {
      updateMetaTag('description', seoDescription);
    }

    // Update meta keywords
    if (seoKeywords) {
      updateMetaTag('keywords', seoKeywords);
    }

    // Update robots meta tag
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }

    // Update canonical URL
    if (canonicalUrl) {
      updateLinkTag('canonical', canonicalUrl);
    }

    // Update Open Graph tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:title', seoTitle, true);
    if (seoDescription) {
      updateMetaTag('og:description', seoDescription, true);
    }
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', seoTitle);
    if (seoDescription) {
      updateMetaTag('twitter:description', seoDescription);
    }
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Cleanup function
    return () => {
      // Optionally clean up meta tags on unmount, but usually we want them to persist
    };
  }, [page, isError, seoTitle, seoDescription, seoKeywords, ogImage, canonicalUrl, noIndex, t]);

  if (isLoading) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (isError || !page) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, fontStyle, { color: colors.error }]}>
          {t('errors.pageNotFound') || 'Page not found'}
        </Text>
      </View>
    );
  }

  const renderContent = () => {
    if (!page.contentHtml) {
      return (
        <View style={styles.noContentContainer}>
          <Text style={[styles.noContent, { color: colors.textSecondary }]}>
            {t('errors.noContent') || 'No content available'}
          </Text>
        </View>
      );
    }

    const htmlDocument = generateHtmlDocument(page.contentHtml);

    // Web platform: use dangerouslySetInnerHTML for better performance
    if (Platform.OS === 'web') {
      return (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {page.featuredImage && (
            <Image
              source={{ uri: page.featuredImage }}
              style={styles.featuredImage}
              contentFit="cover"
            />
          )}
          <Text style={[styles.title, fontStyle, { color: colors.text }]}>
            {page.title}
          </Text>
          <div
            style={{ color: colors.text }}
            dangerouslySetInnerHTML={{ __html: page.contentHtml }}
          />
          <style>{themeCSS}</style>
        </ScrollView>
      );
    }

    // Native platforms: use WebView
    return (
      <View style={styles.nativeContentContainer}>
        {page.featuredImage && (
          <Image
            source={{ uri: page.featuredImage }}
            style={styles.featuredImage}
            contentFit="cover"
          />
        )}
        <Text style={[styles.title, fontStyle, { color: colors.text }]}>
          {page.title}
        </Text>
        <WebView
          source={{ html: htmlDocument }}
          style={[
            styles.webView,
            { backgroundColor: colors.background },
          ]}
          scrollEnabled={true}
          showsVerticalScrollIndicator={false}
          onShouldStartLoadWithRequest={handleWebViewRequest}
          originWhitelist={['*']}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          startInLoadingState={true}
          renderLoading={() => (
            <View style={styles.webViewLoading}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          )}
        />
      </View>
    );
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={['bottom']}
    >
      {showSidebars ? (
        // Web layout with sidebar - parent scroll, sticky sidebar, centered content
        <ScrollView
          style={styles.webScrollContainer}
          contentContainerStyle={styles.webScrollContent}
          showsVerticalScrollIndicator={true}
        >
          <View style={styles.webLayout}>
            {/* Left Sidebar - Sticky */}
            <View style={styles.stickySidebar}>
              <LeftSidebar />
            </View>
            <View style={styles.mainContent}>{renderContent()}</View>
          </View>
        </ScrollView>
      ) : (
        <View style={styles.mobileLayout}>{renderContent()}</View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
  },
  webScrollContainer: {
    flex: 1,
  },
  webScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
  },
  webLayout: {
    flexDirection: 'row',
    width: '100%',
    maxWidth: 1040, // 240 (sidebar) + 800 (content)
  },
  stickySidebar: {
    ...(Platform.OS === 'web' && {
      // @ts-ignore - 'sticky' is valid on web but not in RN types
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      height: 'auto',
    }),
  },
  mobileLayout: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 680,
      width: '100%',
      alignSelf: 'center',
    }),
  },
  mainContent: {
    flex: 1,
    maxWidth: 800,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  nativeContentContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  featuredImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  webView: {
    flex: 1,
    marginHorizontal: -16,
  },
  webViewLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noContentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  noContent: {
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});
